import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  Plus, 
  Wallet, 
  Download, 
  Upload, 
  Calculator, 
  Info, 
  Eye,
  Trash2,
  Pencil, 
  AlertTriangle,
  X,
  Check,
  Search
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import ClotureCaisseWizard from './ClotureCaisseWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0) + ' DA';
};

const ClotureCaisseTab = ({ headers, currentUser, pharmacyIds, wizardOpen, setWizardOpen, dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [closures, setClosures] = useState([]);
  const [currentState, setCurrentState] = useState(null);
  
  // Modals state
  const [deletingClosureId, setDeletingClosureId] = useState(null);
  const [editingClosure, setEditingClosure] = useState(null);
  const [viewingClosure, setViewingClosure] = useState(null);
  
  // State for new closure wizard
  const [countedCash, setCountedCash] = useState('');
  const [notes, setNotes] = useState('');

  
  const confirmDelete = async () => {
    if (!deletingClosureId) return;
    try {
      await axios.delete(`${API}/periodic-closures/${deletingClosureId}`, { headers });
      toast.success("Clôture supprimée avec succès");
      setDeletingClosureId(null);
      fetchState();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression de la clôture");
    }
  };

  const handleDelete = (id) => {
    setDeletingClosureId(id);
  };

  const handleEdit = (c) => {
    setEditingClosure(c);
  };

const fetchState = async () => {
    try {
      setLoading(true);
      const params = { pharmacy_ids: pharmacyIds };
      if (dateRange?.from) {
        params.start_date = dateRange.from.toISOString();
      }
      if (dateRange?.to) {
        const end = new Date(dateRange.to);
        end.setHours(23, 59, 59, 999);
        params.end_date = end.toISOString();
      }
      const results = await Promise.allSettled([
        axios.get(`${API}/periodic-closures/current-state`, { params, headers }),
        axios.get(`${API}/periodic-closures`, { params, headers })
      ]);
      setCurrentState(results[0].status === 'fulfilled' ? results[0].value.data : null);
      setClosures(results[1].status === 'fulfilled' ? (results[1].value.data || []) : []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des données de clôture');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, [pharmacyIds, dateRange]);

  const handleWizardSuccess = () => {
    setWizardOpen(false);
    setEditingClosure(null);
    fetchState();
  };

  if (loading && !currentState) {
    return <div className="p-8 text-center text-gray-500">Chargement...</div>;
  }

  const latestClosure = closures && closures.length > 0 ? closures[0] : null;

  const totalClotureCash = closures.reduce((acc, c) => acc + c.montant_compte, 0);
  const totalTheorique = closures.reduce((acc, c) => acc + c.solde_theorique, 0);
  const totalEcart = closures.reduce((acc, c) => acc + c.ecart, 0);

  return (
    <div className="space-y-6 mt-4">
      {/* 3 KPIs CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        
        {/* Card 1 */}
        <Card className="rounded-xl border border-slate-200 border-l-4 border-l-blue-500 bg-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-blue-50 text-blue-600">
                <Wallet size={22} />
              </div>
              <div className="font-semibold text-slate-800">Total Cash Compté</div>
            </div>
            <div className="text-2xl font-black text-blue-600">{formatCurrency(totalClotureCash)}</div>
            <div className="mt-3 text-xs text-slate-400">Total constaté lors des clôtures</div>
          </CardContent>
        </Card>

        {/* Card 2 */}
        <Card className="rounded-xl border border-slate-200 border-l-4 border-l-purple-500 bg-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-purple-50 text-purple-600">
                <Calculator size={22} />
              </div>
              <div className="font-semibold text-slate-800">Total Solde Théorique</div>
            </div>
            <div className="text-2xl font-black text-purple-600">{formatCurrency(totalTheorique)}</div>
            <div className="mt-3 text-xs text-slate-400">Flux net théorique cumulé</div>
          </CardContent>
        </Card>

        {/* Card 3 */}
        <Card className={`rounded-xl border border-slate-200 border-l-4 ${totalEcart < 0 ? 'border-l-red-500' : totalEcart > 0 ? 'border-l-emerald-500' : 'border-l-blue-500'} bg-white shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer`}>
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-4">
              <div className={`grid h-11 w-11 place-items-center rounded-full ${totalEcart < 0 ? 'bg-red-50 text-red-600' : totalEcart > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                <Calculator size={22} />
              </div>
              <div className="font-semibold text-slate-800">Écart Global</div>
            </div>
            <div className={`text-2xl font-bold ${totalEcart < 0 ? 'text-red-600' : totalEcart > 0 ? 'text-emerald-600' : 'text-blue-600'}`}>
              {totalEcart > 0 ? '+' : ''}{formatCurrency(totalEcart)}
            </div>
            <div className="mt-3 text-xs text-slate-400">
              {totalEcart === 0 ? "Caisse équilibrée" : totalEcart > 0 ? "Excédent (Plus)" : "Déficit (Moins)"}
            </div>
          </CardContent>
        </Card>
      </div>



      {/* ÉTAT DE LA CAISSE (LATEST CLOSURE) */}
      {latestClosure && (
        <div className="space-y-4 pt-4">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#F97316]" />
            <h3 className="text-lg font-bold text-gray-900">État de la caisse</h3>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm relative">
            {/* Box 1 */}
            <div className="flex-1 w-full bg-blue-50/50 border border-blue-100 rounded-xl p-6 text-center relative">
              <p className="text-sm font-bold text-blue-900 flex items-center justify-center gap-1.5 mb-2">
                Solde théorique <Info size={14} className="text-blue-400" />
              </p>
              <p className="text-3xl font-bold text-blue-700 mb-2">{formatCurrency(latestClosure.solde_theorique)}</p>
              <p className="text-xs text-gray-500 mb-1">Solde début + Entrées - Sorties</p>
              <p className="text-xs font-mono text-gray-400">
                {formatCurrency(latestClosure.solde_debut).replace(' DA','')} + {formatCurrency(latestClosure.total_entrees).replace(' DA','')} - {formatCurrency(latestClosure.total_sorties).replace(' DA','')}
              </p>
            </div>
            
            {/* Divider = */}
            <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center font-bold text-gray-500 absolute left-1/2 lg:left-[33.33%] top-full lg:top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              =
            </div>

            {/* Box 2 */}
            <div className="flex-1 w-full bg-emerald-50/50 border border-emerald-100 rounded-xl p-6 text-center relative mt-4 lg:mt-0">
              <p className="text-sm font-bold text-emerald-900 flex items-center justify-center gap-1.5 mb-2">
                Cash réellement compté <Info size={14} className="text-emerald-400" />
              </p>
              <p className="text-3xl font-bold text-emerald-700 mb-2">{formatCurrency(latestClosure.montant_compte)}</p>
              <p className="text-xs text-gray-500">Montant saisi lors de la clôture</p>
            </div>

            {/* Divider - */}
            <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center font-bold text-gray-500 absolute left-1/2 lg:left-[66.66%] top-full lg:top-1/2 -translate-x-1/2 -translate-y-[calc(100%+16px)] lg:-translate-y-1/2 z-10">
              -
            </div>

            {/* Box 3 */}
            <div className={`flex-1 w-full rounded-xl p-6 text-center mt-4 lg:mt-0 border ${latestClosure.ecart < 0 ? 'bg-red-50/50 border-red-100' : latestClosure.ecart > 0 ? 'bg-blue-50/50 border-blue-100' : 'bg-gray-50/50 border-gray-100'}`}>
              <p className={`text-sm font-bold flex items-center justify-center gap-1.5 mb-2 ${latestClosure.ecart < 0 ? 'text-red-900' : latestClosure.ecart > 0 ? 'text-blue-900' : 'text-gray-900'}`}>
                Écart de caisse <Info size={14} className={latestClosure.ecart < 0 ? 'text-red-400' : latestClosure.ecart > 0 ? 'text-blue-400' : 'text-gray-400'} />
              </p>
              <p className={`text-3xl font-bold mb-2 ${latestClosure.ecart < 0 ? 'text-red-600' : latestClosure.ecart > 0 ? 'text-blue-600' : 'text-gray-700'}`}>
                {latestClosure.ecart > 0 ? '+' : ''}{formatCurrency(latestClosure.ecart)}
              </p>
              <p className="text-xs text-gray-500">Cash compté - Solde théorique</p>
            </div>
          </div>
          
          {latestClosure.ecart !== 0 && (
            <div className={`p-4 rounded-xl flex items-center gap-3 ${latestClosure.ecart < 0 ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'}`}>
              <div className={`w-8 h-8 rounded-full flex flex-shrink-0 items-center justify-center text-white ${latestClosure.ecart < 0 ? 'bg-red-500' : 'bg-blue-500'}`}>
                <AlertTriangle size={16} />
              </div>
              <div>
                <p className="text-sm font-bold">Écart détecté : {latestClosure.ecart > 0 ? '+' : ''}{formatCurrency(latestClosure.ecart)}</p>
                <p className="text-xs mt-0.5">Veuillez fournir une justification lors de la clôture.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOUVELLE CLÔTURE WIZARD */}
      {(wizardOpen || editingClosure) && (
        <ClotureCaisseWizard 
          onClose={() => { setWizardOpen(false); setEditingClosure(null); }}
          onSuccess={handleWizardSuccess}
          headers={headers}
          pharmacyIds={pharmacyIds}
          editData={editingClosure}
          initialDateRange={dateRange}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deletingClosureId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Supprimer la clôture</h3>
              <p className="text-gray-500 text-sm">
                Êtes-vous sûr de vouloir supprimer définitivement cette clôture ? Cette action ne peut pas être annulée.
              </p>
            </div>
            <div className="bg-gray-50 p-4 flex gap-3 sm:px-8 sm:pb-8 sm:pt-4">
              <button 
                onClick={() => setDeletingClosureId(null)}
                className="flex-1 px-4 py-3 rounded-xl font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-md shadow-red-500/20 transition-all active:scale-95"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEWING MODAL */}
      {viewingClosure && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#F97316]" />
                Détails de la clôture
              </h3>
              <button onClick={() => setViewingClosure(null)} className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4 bg-gray-50/30">
              <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                <span className="text-sm font-medium text-gray-500">Période</span>
                <span className="text-sm font-bold text-gray-900">
                  {format(new Date(viewingClosure.period_start), 'dd MMM', { locale: fr })} - {format(new Date(viewingClosure.period_end), 'dd MMM yyyy', { locale: fr })}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                <span className="text-sm font-medium text-gray-500">Solde de début</span>
                <span className="text-sm font-bold text-gray-600">{formatCurrency(viewingClosure.solde_debut)}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm text-center">
                  <span className="text-xs font-medium text-gray-500 block mb-1">Entrées</span>
                  <span className="text-sm font-bold text-emerald-600">+{formatCurrency(viewingClosure.total_entrees)}</span>
                </div>
                <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm text-center">
                  <span className="text-xs font-medium text-gray-500 block mb-1">Sorties</span>
                  <span className="text-sm font-bold text-red-600">-{formatCurrency(viewingClosure.total_sorties)}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                <span className="text-sm font-medium text-gray-500">Solde théorique</span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(viewingClosure.solde_theorique)}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-[#5B21B6]">
                <span className="text-sm font-medium text-[#5B21B6]">Montant compté</span>
                <span className="text-sm font-black text-[#5B21B6]">{formatCurrency(viewingClosure.montant_compte)}</span>
              </div>
              
              <div className={`flex justify-between items-center p-4 rounded-xl shadow-inner ${viewingClosure.ecart < 0 ? 'bg-red-50 text-red-700' : viewingClosure.ecart > 0 ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
                <span className="text-sm font-bold flex items-center gap-2">
                  {viewingClosure.ecart < 0 ? <AlertTriangle size={16} /> : <Check size={16} />}
                  Écart constaté
                </span>
                <span className="text-lg font-black">
                  {viewingClosure.ecart > 0 ? '+' : ''}{formatCurrency(viewingClosure.ecart)}
                </span>
              </div>
            </div>
            
            <div className="p-4 bg-white border-t border-gray-100">
              <button 
                onClick={() => setViewingClosure(null)}
                className="w-full px-4 py-3 rounded-xl font-bold text-gray-700 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TABLE */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-col">
            <h3 className="text-lg font-black text-gray-900">Historique des Clôtures de Caisse</h3>
            <p className="text-sm text-gray-500 mt-1">Clôtures périodiques et suivi des écarts</p>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Rechercher..." 
                className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 px-4 h-11 rounded-lg border border-gray-200 transition-colors">
              <Download size={16} />
              Exporter
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Période</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Solde début</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Entrées</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Sorties</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Solde théorique</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Compté</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Écart</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center">Statut</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {closures.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500">
                    Aucune clôture trouvée
                  </td>
                </tr>
              ) : closures.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {format(new Date(c.period_start), 'dd MMM', { locale: fr })} - {format(new Date(c.period_end), 'dd MMM yyyy', { locale: fr })}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-600">{formatCurrency(c.solde_debut)}</td>
                  <td className="px-6 py-4 text-sm font-medium text-emerald-600">+{formatCurrency(c.total_entrees)}</td>
                  <td className="px-6 py-4 text-sm font-medium text-red-600">-{formatCurrency(c.total_sorties)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">{formatCurrency(c.solde_theorique)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">{formatCurrency(c.montant_compte)}</td>
                  <td className={`px-6 py-4 text-sm font-bold ${c.ecart < 0 ? 'text-red-600' : c.ecart > 0 ? 'text-blue-600' : 'text-emerald-600'}`}>
                    {c.ecart > 0 ? '+' : ''}{formatCurrency(c.ecart)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${c.ecart < 0 ? 'bg-red-50 text-red-700 border-red-200' : c.ecart > 0 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                      {c.ecart < 0 ? <Wallet size={12} /> : <Check size={12} />}
                      CLÔTURÉE
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setViewingClosure(c)} className="p-2 text-gray-400 hover:text-gray-900 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow transition-all" title="Voir">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => handleEdit(c)} className="p-2 text-blue-400 hover:text-blue-600 bg-white border border-blue-200 rounded-lg shadow-sm hover:shadow transition-all" title="Modifier">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="p-2 text-red-400 hover:text-red-600 bg-white border border-red-200 rounded-lg shadow-sm hover:shadow transition-all" title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-center">
          <p className="text-xs font-semibold text-gray-500">{closures.length} clôture(s)</p>
        </div>
      </div>
    </div>
  );
};

export default ClotureCaisseTab;
